import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { IS_DEMO } from "@/lib/mode";
import {
  ChevronLeft,
  Key,
  Copy,
  Plus,
  Trash2,
  Webhook,
  Terminal,
  ShieldCheck,
  Activity,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { AppShell } from "@/components/app-shell";
import { DCard, HeroCard, Pill, SectionHeader, Eyebrow, SoftRule } from "@/components/dryvn";

export const Route = createFileRoute("/more/settings/api")({
  // LANE 2 live-gating (2026-07-10): the API/webhooks surface is not
  // functional in live — key minting is inert, webhook create returns
  // 501 without KMS, and several controls have no handler. Demo-only;
  // live redirects to the settings hub so no dead developer surface ships.
  beforeLoad: () => {
    if (!IS_DEMO) throw redirect({ to: "/more/settings" });
  },
  head: () => ({
    meta: [
      { title: "API & webhooks — Dryvn Life" },
      {
        name: "description",
        content: "Personal API keys, webhooks, and developer access for Dryvn Life.",
      },
    ],
  }),
  component: ApiPage,
});

import type { ApiKeyRecord, WebhookRecord, ApiUsageStat } from "@/data/types";
// Screen migration (2026-07-05, ruling 2h): webhooks via hook; api keys +
// usage are demo-only until the automation_tokens scope surface ships —
// live starts empty and key-minting is inert (never a fake dryv_live_sk_).
import { initialApiKeys, apiUsage } from "@/data/mock";
import { useWebhooks } from "@/lib/queries";

// Local aliases keep the rest of this file unchanged.
type ApiKey = ApiKeyRecord;
type WebhookT = WebhookRecord;
type Usage = ApiUsageStat;
void ({} as WebhookT);
void ({} as Usage);

const initialKeys = IS_DEMO ? initialApiKeys : ([] as ApiKeyRecord[]);
const usage = IS_DEMO ? apiUsage : apiUsage.map((u) => ({ ...u, value: "—", delta: "" }));

function ApiPage() {
  const { data: webhooks } = useWebhooks();
  const [keys, setKeys] = useState(initialKeys);
  const [showCreated, setShowCreated] = useState<string | null>(null);

  const createKey = () => {
    if (!IS_DEMO) return; // arriving after round-1 (owner-only mint, server-side)
    const id = `k${keys.length + 1}`;
    const secret = `dryv_live_sk_${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 6)}`;
    setKeys([
      ...keys,
      {
        id,
        name: "New key",
        prefix: `dryv_live_pk_${secret.slice(-10, -3)}…${secret.slice(-3)}`,
        scopes: ["read:today"],
        created: "just now",
        lastUsed: "never",
      },
    ]);
    setShowCreated(secret);
  };

  return (
    <AppShell>
      <Link
        to="/more/settings"
        className="mb-4 inline-flex items-center gap-1 text-sm"
        style={{ color: "var(--dryvn-grey)" }}
      >
        <ChevronLeft className="h-4 w-4" /> Settings
      </Link>

      <HeroCard
        eyebrow="Developer"
        title={
          <>
            Build{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              on
            </em>{" "}
            your life.
          </>
        }
      >
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="life">API v1 · stable</Pill>
          <Pill tone="primary">{keys.length} keys</Pill>
          <Pill tone="muted">{webhooks.length} webhooks</Pill>
        </div>
      </HeroCard>

      {showCreated && (
        <DCard className="mt-6" style={{ borderColor: "var(--dryvn-primary)" }}>
          <div className="flex items-start gap-3">
            <ShieldCheck
              className="mt-0.5 h-5 w-5 shrink-0"
              style={{ color: "var(--dryvn-primary)" }}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                Key created — copy it now
              </p>
              <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                We won't show the secret again. Store it somewhere safe.
              </p>
              <div
                className="mt-3 flex items-center gap-2 rounded-lg px-3 py-2 font-mono text-xs"
                style={{
                  backgroundColor: "var(--dryvn-page-tint)",
                  color: "var(--dryvn-primary-deep)",
                }}
              >
                <span className="min-w-0 flex-1 truncate">{showCreated}</span>
                <button
                  onClick={() => navigator.clipboard?.writeText(showCreated)}
                  className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium text-white"
                  style={{ backgroundColor: "var(--dryvn-primary)" }}
                >
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowCreated(null)}
              className="text-xs"
              style={{ color: "var(--dryvn-grey)" }}
            >
              Dismiss
            </button>
          </div>
        </DCard>
      )}

      <div className="mt-8">
        <SectionHeader
          title="API keys"
          eyebrow="Access"
          action={
            <button
              onClick={createKey}
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium text-white"
              style={{ backgroundColor: "var(--dryvn-primary)" }}
            >
              <Plus className="h-3.5 w-3.5" /> New key
            </button>
          }
        />
        <div className="grid gap-3">
          {keys.map((k) => (
            <DCard key={k.id} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                style={{
                  backgroundColor: "color-mix(in oklab, var(--dryvn-life) 30%, transparent)",
                }}
              >
                <Key className="h-5 w-5" style={{ color: "var(--dryvn-primary-deep)" }} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                    {k.name}
                  </p>
                  {k.scopes.map((s) => (
                    <Pill key={s} tone="primary">
                      {s}
                    </Pill>
                  ))}
                </div>
                <div
                  className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]"
                  style={{ color: "var(--dryvn-grey)" }}
                >
                  <span className="font-mono">{k.prefix}</span>
                  <span>Created {k.created}</span>
                  <span>Last used {k.lastUsed}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]"
                  style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
                >
                  <Copy className="h-3 w-3" /> Copy id
                </button>
                <button
                  onClick={() => setKeys(keys.filter((x) => x.id !== k.id))}
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]"
                  style={{ borderColor: "var(--dryvn-line)", color: "var(--destructive)" }}
                >
                  <Trash2 className="h-3 w-3" /> Revoke
                </button>
              </div>
            </DCard>
          ))}
        </div>
      </div>

      <SoftRule />

      <div>
        <SectionHeader
          title="Webhooks"
          eyebrow="Events"
          action={
            <button
              className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium text-white"
              style={{ backgroundColor: "var(--dryvn-primary)" }}
            >
              <Plus className="h-3.5 w-3.5" /> New endpoint
            </button>
          }
        />
        <div className="grid gap-3">
          {webhooks.map((w) => (
            <DCard key={w.id} className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <span
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full"
                style={{
                  backgroundColor:
                    w.status === "healthy"
                      ? "color-mix(in oklab, var(--dryvn-life) 30%, transparent)"
                      : "color-mix(in oklab, var(--dryvn-warm) 30%, transparent)",
                }}
              >
                <Webhook
                  className="h-5 w-5"
                  style={{
                    color:
                      w.status === "healthy"
                        ? "var(--dryvn-primary-deep)"
                        : "var(--dryvn-warm-deep)",
                  }}
                />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p
                    className="min-w-0 truncate font-mono text-sm"
                    style={{ color: "var(--dryvn-ink)" }}
                  >
                    {w.url}
                  </p>
                  {w.status === "healthy" ? (
                    <Pill tone="life">
                      <CheckCircle2 className="mr-1 h-3 w-3" /> Healthy
                    </Pill>
                  ) : (
                    <Pill tone="warm">
                      <AlertCircle className="mr-1 h-3 w-3" /> Failing
                    </Pill>
                  )}
                </div>
                <div
                  className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]"
                  style={{ color: "var(--dryvn-grey)" }}
                >
                  <span>{w.events.join(", ")}</span>
                  <span>Last delivery {w.lastDelivery}</span>
                </div>
              </div>
              <button
                className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px]"
                style={{ borderColor: "var(--dryvn-line)", color: "var(--dryvn-ink)" }}
              >
                <Activity className="h-3 w-3" /> Logs
              </button>
            </DCard>
          ))}
        </div>
      </div>

      <SoftRule />

      <div>
        <SectionHeader title="Usage" eyebrow="Last 30 days" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {usage.map((u) => (
            <DCard key={u.label}>
              <Eyebrow>{u.label}</Eyebrow>
              <p className="mt-1 font-display text-3xl" style={{ color: "var(--dryvn-ink)" }}>
                {u.value}
              </p>
              <p className="mt-1 text-xs" style={{ color: "var(--dryvn-grey)" }}>
                {u.delta}
              </p>
            </DCard>
          ))}
        </div>
      </div>

      <div className="mt-8">
        <DCard>
          <div className="flex items-start gap-3">
            <Terminal
              className="mt-0.5 h-5 w-5 shrink-0"
              style={{ color: "var(--dryvn-primary-deep)" }}
            />
            <div className="min-w-0 flex-1">
              <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                Quick start
              </p>
              <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                Every endpoint accepts a bearer token.
              </p>
              <pre
                className="mt-3 overflow-x-auto rounded-lg p-3 font-mono text-[11px] leading-relaxed"
                style={{ backgroundColor: "var(--dryvn-page-tint)", color: "var(--dryvn-ink)" }}
              >{`curl https://api.dryvn.life/v1/today \\
  -H "Authorization: Bearer $DRYVN_LIVE_KEY" \\
  -H "Accept: application/json"`}</pre>
            </div>
          </div>
        </DCard>
      </div>
    </AppShell>
  );
}
